import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { streamAI } from "@/lib/stream";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Send, Globe, AlertTriangle, Leaf, Download } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import DiscoveryPanel from "@/components/DiscoveryPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

function Dashboard() {
  const [stats, setStats] = useState({});
  const [accs, setAccs] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [resolve, setResolve] = useState(null); // { orderId, outcome, title }
  const [adminNote, setAdminNote] = useState("");
  const [summary, setSummary] = useState(null); // { accId, text, streaming }

  const aiSummary = async (accId) => {
    setSummary({ accId, text: "", streaming: true });
    try {
      await streamAI(`/ai/compliance-summary/${accId}`, { onDelta: (t) => setSummary((s) => s && s.accId === accId ? { ...s, text: s.text + t } : s) });
      setSummary((s) => s ? { ...s, streaming: false } : s);
    } catch (e) { toast.error(formatApiError(e)); setSummary(null); }
  };

  const [assocs, setAssocs] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [assocSearch, setAssocSearch] = useState("");
  const [assocPage, setAssocPage] = useState(0);
  const [draft, setDraft] = useState(null); // { assocId, name, text, streaming }
  const loadAssocs = () => api.get("/associations").then(r => setAssocs(r.data.associations)).catch(() => {});
  useEffect(() => { loadAssocs(); }, []);

  const draftOutreach = async (a) => {
    setDraft({ assocId: a.id, name: a.name, email: a.email, text: "", streaming: true });
    try {
      await streamAI(`/ai/association-outreach/${a.id}`, { method: "POST", onDelta: (t) => setDraft((s) => s && s.assocId === a.id ? { ...s, text: s.text + t } : s) });
      setDraft((s) => s ? { ...s, streaming: false } : s);
      loadAssocs();
    } catch (e) { toast.error(formatApiError(e)); setDraft(null); }
  };

  const sendOutreach = async (assocId) => {
    try {
      await api.post(`/associations/${assocId}/send`);
      toast.success("Outreach email sent");
      setDraft(null);
      loadAssocs();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reload = () => {
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/admin/accreditations").then(r => setAccs(r.data.records));
    api.get("/admin/users").then(r => setUsers(r.data.users));
    api.get("/admin/orders").then(r => setOrders(r.data.orders));
    api.get("/admin/disputes").then(r => setDisputes(r.data.disputes)).catch(() => {});
  };
  useEffect(reload, []);

  const decide = async (id, decision) => {
    try { await api.post(`/admin/accreditations/${id}/decision`, null, { params: { decision } }); toast.success(`Marked ${decision}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const ASSOC_PAGE_SIZE = 25;
  const filteredAssocs = assocs
    .filter(a => catFilter === "all" || (a.category || "association") === catFilter)
    .filter(a => !assocSearch || `${a.name} ${a.country} ${a.email || ""} ${a.related_breed || ""}`.toLowerCase().includes(assocSearch.toLowerCase()));
  const assocPages = Math.max(1, Math.ceil(filteredAssocs.length / ASSOC_PAGE_SIZE));
  const pageAssocs = filteredAssocs.slice(assocPage * ASSOC_PAGE_SIZE, (assocPage + 1) * ASSOC_PAGE_SIZE);
  const dueCount = assocs.filter(a => a.follow_up_due).length;

  const [crm, setCrm] = useState(null); // { assoc, record }
  const [crmStage, setCrmStage] = useState("new");
  const [crmNote, setCrmNote] = useState("");
  const [crmFollow, setCrmFollow] = useState("7");

  const openCrm = async (a) => {
    try {
      const { data } = await api.get(`/associations/${a.id}/crm`);
      setCrm({ assoc: a, record: data.crm });
      setCrmStage(data.crm.stage || "new");
      setCrmNote("");
      setCrmFollow("7");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveCrm = async () => {
    try {
      const { data } = await api.post(`/associations/${crm.assoc.id}/crm`, {
        stage: crmStage, note: crmNote, follow_up_days: crmFollow === "" ? null : parseInt(crmFollow),
      });
      setCrm({ ...crm, record: data.crm });
      setCrmNote("");
      toast.success("CRM record saved");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resolveDispute = async () => {
    try {
      await api.post(`/admin/disputes/${resolve.orderId}/resolve`, { outcome: resolve.outcome, admin_note: adminNote });
      toast.success(`Dispute resolved: ${resolve.outcome}`);
      setResolve(null); setAdminNote(""); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [dadis, setDadis] = useState(null);
  const [dadisSyncing, setDadisSyncing] = useState(false);
  const loadDadis = () => api.get("/admin/dadis/status").then(r => setDadis(r.data)).catch(() => {});
  useEffect(() => { loadDadis(); }, []);

  const exportOutreach = async () => {
    try {
      const r = await api.get("/associations/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "geneticstrade_outreach_contacts.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Outreach contacts exported as CSV");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runDadisSync = async () => {
    setDadisSyncing(true);
    try {
      await api.post("/admin/dadis/sync");
      toast.success("FAO DAD-IS sync started — takes about a minute");
      setTimeout(loadDadis, 5000);
      setTimeout(loadDadis, 30000);
      setTimeout(() => { loadDadis(); setDadisSyncing(false); }, 75000);
    } catch (e) { toast.error(formatApiError(e)); setDadisSyncing(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="heading text-3xl font-extrabold tracking-tight">Admin Console</h1>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat k="Users" v={stats.users_total} />
        <Stat k="Pending sellers" v={stats.sellers_pending} />
        <Stat k="Active lots" v={stats.lots_active} />
        <Stat k="Open orders" v={stats.orders_open} />
      </div>

      <Tabs defaultValue="acc" className="mt-8">
        <TabsList>
          <TabsTrigger value="acc" data-testid="tab-acc">Accreditations</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="tab-disputes">Disputes{disputes.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] mono">{disputes.length}</span>}</TabsTrigger>
          <TabsTrigger value="outreach" data-testid="tab-outreach">AI Outreach</TabsTrigger>
          <TabsTrigger value="discovery" data-testid="tab-discovery">Discovery</TabsTrigger>
          <TabsTrigger value="conservation" data-testid="tab-conservation">Conservation</TabsTrigger>
        </TabsList>
        <TabsContent value="acc" className="mt-4">
          <div className="surface-card overflow-hidden">
            <table className="data-table">
              <thead><tr><th>Organization</th><th>License</th><th>Country</th><th>Certs</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {accs.map(a => (
                  <tr key={a.id}>
                    <td>{a.organization}</td>
                    <td className="mono text-xs">{a.license_number}</td>
                    <td>{a.country}</td>
                    <td className="mono text-xs">{(a.lab_certifications || []).join(", ")}</td>
                    <td><span className={`text-xs mono uppercase px-2 py-1 rounded-full ${a.status === "verified" ? "pill-recovering" : a.status === "rejected" ? "pill-critical" : "pill-watch"}`}>{a.status}</span></td>
                    <td className="flex gap-2">
                      <Button data-testid={`acc-ai-summary-${a.id}`} size="sm" variant="outline" onClick={() => aiSummary(a.id)}>
                        <Sparkles className="h-3.5 w-3.5 mr-1" /> AI summary
                      </Button>
                      {a.status === "pending" && <>
                        <Button data-testid={`acc-approve-${a.id}`} size="sm" className="btn-forest" onClick={() => decide(a.id, "verified")}>Approve</Button>
                        <Button data-testid={`acc-reject-${a.id}`} size="sm" variant="outline" onClick={() => decide(a.id, "rejected")}>Reject</Button>
                      </>}
                    </td>
                  </tr>
                ))}
                {accs.length === 0 && <tr><td colSpan={6} className="text-stone-500 text-center py-6">No submissions.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="users" className="mt-4">
          <div className="surface-card overflow-hidden">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Accreditation</th><th>Rating</th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id}>
                    <td>{u.name}</td>
                    <td className="mono text-xs">{u.email}</td>
                    <td className="uppercase mono text-xs">{u.role}</td>
                    <td className="mono text-xs">{u.accreditation_status}</td>
                    <td className="mono">{u.rating_avg?.toFixed(1) || "—"} ({u.rating_count || 0})</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="orders" className="mt-4">
          <div className="surface-card overflow-hidden">
            <table className="data-table">
              <thead><tr><th>Order</th><th>Buyer</th><th>Seller</th><th>Total</th><th>Status</th></tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.order_id}>
                    <td className="mono text-xs">{o.order_id}</td>
                    <td>{o.buyer_name}</td>
                    <td>{o.seller_name}</td>
                    <td className="mono">${o.total?.toFixed(2)}</td>
                    <td><span className="text-xs mono uppercase px-2 py-1 rounded-full pill-watch">{o.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="disputes" className="mt-4">
          <div className="surface-card overflow-hidden">
            <table className="data-table">
              <thead><tr><th>Order</th><th>Parties</th><th>Total</th><th>Dispute</th><th></th></tr></thead>
              <tbody>
                {disputes.map(o => (
                  <tr key={o.order_id} data-testid={`dispute-row-${o.order_id}`}>
                    <td className="mono text-xs">{o.order_id}</td>
                    <td className="text-xs">{o.buyer_name} vs {o.seller_name}</td>
                    <td className="mono">${o.total?.toFixed(2)}</td>
                    <td>
                      <div className="text-xs font-medium text-red-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {o.dispute?.reason}</div>
                      {o.dispute?.details && <div className="text-xs text-stone-500 mt-0.5 max-w-xs">{o.dispute.details}</div>}
                      <div className="text-[10px] text-stone-400 mono mt-0.5">{o.dispute?.raised_at?.slice(0, 10)} · was {o.dispute?.from_status}</div>
                    </td>
                    <td className="flex gap-2 flex-wrap">
                      <Button data-testid={`dispute-release-${o.order_id}`} size="sm" className="btn-forest" onClick={() => { setResolve({ orderId: o.order_id, outcome: "release", title: "Release escrow to seller" }); setAdminNote(""); }}>Release</Button>
                      <Button data-testid={`dispute-refund-${o.order_id}`} size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => { setResolve({ orderId: o.order_id, outcome: "refund", title: "Refund buyer (void payment hold)" }); setAdminNote(""); }}>Refund</Button>
                      <Button data-testid={`dispute-resume-${o.order_id}`} size="sm" variant="outline" onClick={() => { setResolve({ orderId: o.order_id, outcome: "resume", title: "Dismiss dispute & resume order" }); setAdminNote(""); }}>Resume</Button>
                    </td>
                  </tr>
                ))}
                {disputes.length === 0 && <tr><td colSpan={5} className="text-stone-500 text-center py-6">No open disputes.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="outreach" className="mt-4">
          <div className="flex items-center gap-3 mb-3">
            <Select value={catFilter} onValueChange={(v) => { setCatFilter(v); setAssocPage(0); }}>
              <SelectTrigger data-testid="outreach-cat-filter" className="w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ({assocs.length})</SelectItem>
                <SelectItem value="association">Breed associations ({assocs.filter(a => (a.category || "association") === "association").length})</SelectItem>
                <SelectItem value="vet_ai">AI veterinarians &amp; repro services ({assocs.filter(a => a.category === "vet_ai").length})</SelectItem>
                <SelectItem value="cryo_transport">Cryo &amp; transport providers ({assocs.filter(a => a.category === "cryo_transport").length})</SelectItem>
                <SelectItem value="stud">Studs &amp; farms ({assocs.filter(a => a.category === "stud").length})</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="outreach-search" placeholder="Search name, country, breed or email…" value={assocSearch} onChange={(e) => { setAssocSearch(e.target.value); setAssocPage(0); }} className="w-72" />
            <Button type="button" variant="outline" size="sm" data-testid="outreach-export-btn" onClick={exportOutreach} className="whitespace-nowrap">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
            </Button>
            <div className="ml-auto flex items-center gap-2 text-xs text-stone-500 mono" data-testid="outreach-pagination">
              <Button type="button" variant="outline" size="sm" data-testid="outreach-prev-page" disabled={assocPage === 0} onClick={() => setAssocPage(p => p - 1)}>Prev</Button>
              <span>Page {assocPage + 1} / {assocPages} · {filteredAssocs.length} targets</span>
              <Button type="button" variant="outline" size="sm" data-testid="outreach-next-page" disabled={assocPage >= assocPages - 1} onClick={() => setAssocPage(p => p + 1)}>Next</Button>
            </div>
            {dueCount > 0 && <span data-testid="outreach-followups-due" className="px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs mono whitespace-nowrap">{dueCount} follow-ups due</span>}
          </div>
          <div className="surface-card overflow-hidden">
            <table className="data-table">
              <thead><tr><th>Association</th><th>Focus</th><th>Breeds / Lots</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {pageAssocs.map(a => (
                  <tr key={a.id} data-testid={`assoc-row-${a.id}`}>
                    <td>
                      <div className="font-medium">{a.name}</div>
                      <div className="text-xs text-stone-500 flex items-center gap-1 flex-wrap"><Globe className="h-3 w-3" /> {a.country}{a.related_breed ? ` · ${a.related_breed}` : ""}{a.category === "vet_ai" && <span className="px-1.5 py-0.5 rounded-full pill-stable">AI vet</span>}{a.category === "cryo_transport" && <span className="px-1.5 py-0.5 rounded-full pill-stable">cryo/transport</span>}{a.category === "stud" && <span className="px-1.5 py-0.5 rounded-full pill-watch">stud</span>}{["vet_ai","cryo_transport","stud"].includes(a.category) ? "" : " · "}{a.email ? <span className="mono">{a.email}</span> : <span className="text-amber-600">contact TBD</span>}</div>
                    </td>
                    <td className="text-xs capitalize">{a.focus_species.join(", ")}</td>
                    <td className="mono text-xs">
                      {a.matched_breeds} breeds · {a.matched_lots} lots
                      {a.matched_lots > 0 && (
                        <div className="text-[10px] text-stone-500 mt-0.5">
                          listed: {Object.entries(a.species_breakdown || {}).map(([sp, n]) => `${sp}×${n}`).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {a.last_sent_at ? <span className="text-xs mono px-2 py-1 rounded-full pill-recovering">sent {a.last_sent_at.slice(0, 10)}</span> : a.has_draft ? <span className="text-xs mono px-2 py-1 rounded-full pill-watch">draft ready</span> : <span className="text-xs text-stone-400">—</span>}
                        {a.stage && a.stage !== "new" && <span data-testid={`assoc-stage-${a.id}`} className="text-[10px] mono uppercase px-1.5 py-0.5 rounded-full pill-stable">{a.stage}</span>}
                        {a.follow_up_due && <span data-testid={`assoc-due-${a.id}`} className="text-[10px] mono uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">follow-up due</span>}
                        {a.notes_count > 0 && <span className="text-[10px] mono text-stone-400">{a.notes_count} note{a.notes_count > 1 ? "s" : ""}</span>}
                      </div>
                    </td>
                    <td className="flex gap-2">
                      <Button data-testid={`assoc-draft-${a.id}`} size="sm" variant="outline" onClick={() => draftOutreach(a)}>
                        <Sparkles className="h-3.5 w-3.5 mr-1" /> {a.has_draft ? "Re-draft" : "Draft with Claude"}
                      </Button>
                      <Button data-testid={`assoc-crm-${a.id}`} size="sm" variant="outline" onClick={() => openCrm(a)}>CRM</Button>
                    </td>
                  </tr>
                ))}
                {assocs.length === 0 && <tr><td colSpan={5} className="text-stone-500 text-center py-6">Loading associations…</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="discovery" className="mt-4">
          <DiscoveryPanel />
        </TabsContent>
        <TabsContent value="conservation" className="mt-4">
          <div className="surface-card p-6 max-w-2xl" data-testid="conservation-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="heading font-semibold flex items-center gap-2"><Leaf className="h-4 w-4 text-[color:var(--forest-deep)]" /> FAO DAD-IS live sync</div>
                <div className="text-xs text-stone-500 mt-1 max-w-md">Pulls the latest national breed risk statuses from the FAO Domestic Animal Diversity Information System (16 countries, all listed livestock species). Curated and user-submitted breeds are never overwritten — a breed keeps the worst status seen across countries. Also runs automatically every Sunday 03:00 UTC.</div>
              </div>
              <Button data-testid="dadis-sync-btn" className="btn-forest" disabled={dadisSyncing || dadis?.last_run?.status === "running"} onClick={runDadisSync}>
                {dadisSyncing || dadis?.last_run?.status === "running" ? "Syncing…" : "Sync now"}
              </Button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs mono" data-testid="dadis-status">
              <div className="border border-stone-200 rounded-md p-3">
                <div className="text-stone-400 uppercase text-[10px] tracking-widest">DAD-IS breeds in database</div>
                <div className="text-lg font-bold mt-1" data-testid="dadis-breed-count">{dadis?.dadis_breeds ?? "—"}</div>
              </div>
              <div className="border border-stone-200 rounded-md p-3">
                <div className="text-stone-400 uppercase text-[10px] tracking-widest">Last run</div>
                <div className="mt-1" data-testid="dadis-last-run">{dadis?.last_run ? `${dadis.last_run.status} · ${(dadis.last_run.finished_at || dadis.last_run.started_at || "").slice(0, 16).replace("T", " ")} UTC` : "never"}</div>
              </div>
              {dadis?.last_run?.status === "done" && (
                <div className="col-span-2 border border-stone-200 rounded-md p-3" data-testid="dadis-last-run-stats">
                  {dadis.last_run.breeds_seen} breeds seen · {dadis.last_run.inserted} added · {dadis.last_run.updated} updated · {dadis.last_run.skipped_curated} curated kept
                </div>
              )}
              {dadis?.last_run?.status === "failed" && (
                <div className="col-span-2 text-red-700" data-testid="dadis-last-error">Last sync failed: {dadis.last_run.error}</div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!crm} onOpenChange={(o) => !o && setCrm(null)}>
        <DialogContent data-testid="assoc-crm-dialog">
          <DialogHeader><DialogTitle>CRM — {crm?.assoc.name}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Manage the outreach CRM record for this target.</DialogDescription>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Stage</label>
              <Select value={crmStage} onValueChange={setCrmStage}>
                <SelectTrigger data-testid="crm-stage-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["new", "drafted", "sent", "replied", "partnered", "declined"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500">Follow up in days (0 = clear)</label>
              <Input data-testid="crm-followup-days" type="number" min={0} value={crmFollow} onChange={(e) => setCrmFollow(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="mt-2">
            <label className="text-xs uppercase tracking-widest text-stone-500">Add note (reply received, meeting booked…)</label>
            <Textarea data-testid="crm-note-input" value={crmNote} onChange={(e) => setCrmNote(e.target.value)} className="mt-1" />
          </div>
          <div data-testid="crm-notes-list" className="max-h-40 overflow-y-auto space-y-1.5 mt-2">
            {(crm?.record.notes || []).slice().reverse().map((n, i) => (
              <div key={i} className="text-xs border border-stone-200 rounded px-2 py-1.5"><span className="mono text-stone-400">{n.at?.slice(0, 10)}</span> — {n.text}</div>
            ))}
            {!(crm?.record.notes || []).length && <div className="text-xs text-stone-400">No notes yet.</div>}
          </div>
          <div className="text-xs text-stone-500 mono">Stage: {crm?.record.stage || "new"}{crm?.record.follow_up_at ? ` · follow-up ${crm.record.follow_up_at.slice(0, 10)}` : ""}{crm?.record.last_reply_at ? ` · last reply ${crm.record.last_reply_at.slice(0, 10)}` : ""}</div>
          <Button data-testid="crm-save-btn" className="btn-earth mt-2" onClick={saveCrm}>Save CRM record</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolve} onOpenChange={(o) => !o && setResolve(null)}>
        <DialogContent data-testid="dispute-resolve-dialog">
          <DialogHeader><DialogTitle>{resolve?.title}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Resolve the open dispute on this order.</DialogDescription>
          <div className="text-sm text-stone-600">Order <span className="mono">{resolve?.orderId}</span></div>
          <Textarea data-testid="dispute-admin-note" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} placeholder="Note to both parties (optional)" className="mt-3" />
          <Button data-testid="dispute-resolve-confirm" className="btn-earth mt-4" onClick={resolveDispute}>Confirm resolution</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!draft} onOpenChange={(o) => !o && !draft?.streaming && setDraft(null)}>
        <DialogContent data-testid="outreach-draft-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[color:var(--earth-amber)]" /> Outreach draft — {draft?.name}</DialogTitle></DialogHeader>
          <div data-testid="outreach-draft-text" className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed max-h-[50vh] overflow-y-auto border border-stone-200 rounded-md p-4 bg-stone-50">
            {draft?.text}{draft?.streaming && <span className="inline-block w-2 h-4 bg-[color:var(--earth-amber)] animate-pulse" />}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[10px] mono uppercase text-stone-400">Claude Sonnet 5 · review before sending</div>
            {draft?.email ? (
              <Button data-testid="outreach-send-btn" className="btn-forest" disabled={!draft || draft.streaming || !draft.text} onClick={() => sendOutreach(draft.assocId)}>
                <Send className="h-3.5 w-3.5 mr-1" /> Send to association
              </Button>
            ) : (
              <span className="text-xs text-amber-700" data-testid="outreach-no-contact">No contact email on file — add one to enable sending</span>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!summary} onOpenChange={(o) => !o && setSummary(null)}>
        <DialogContent data-testid="ai-compliance-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[color:var(--earth-amber)]" /> AI Compliance Summary</DialogTitle></DialogHeader>
          <div data-testid="ai-compliance-text" className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto">
            {summary?.text}{summary?.streaming && <span className="inline-block w-2 h-4 bg-[color:var(--earth-amber)] animate-pulse" />}
          </div>
          <div className="text-[10px] mono uppercase text-stone-400">Claude Sonnet 5 · advisory only — final decision is yours</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="surface-card p-5">
      <div className="text-[10px] uppercase tracking-widest text-stone-400">{k}</div>
      <div className="heading text-3xl font-extrabold mono mt-1">{v ?? "—"}</div>
    </div>
  );
}

export default function AdminConsole() { return <Routes><Route index element={<Dashboard />} /><Route path="*" element={<Navigate to="/admin" replace />} /></Routes>; }
